import React, { useState } from 'react';
import HelpTip from './HelpTip';
import { useTranslation } from 'react-i18next';

function calcRiskScore(pathInfo, subgraphNodes) {
  if (!pathInfo || !pathInfo.details) return { score: 0, level: 'low', insolvent: 0, foreign: 0 };

  const nodeMap = {};
  if (subgraphNodes) {
    subgraphNodes.forEach(n => {
      const d = n.data || n;
      nodeMap[d.id] = d;
    });
  }

  let insolventCount = 0;
  let foreignCount = 0;

  pathInfo.details.forEach(step => {
    // Try to find matching node in subgraph for extra data
    const nodeId = step.id || step.name;
    const nodeData = nodeMap[nodeId] || {};

    if (nodeData.insolvent || step.insolvent) insolventCount++;
    if ((nodeData.country && nodeData.country !== 'CZ') || (step.country && step.country !== 'CZ')) foreignCount++;
  });

  const score = insolventCount + foreignCount;
  const level = score === 0 ? 'low' : score <= 2 ? 'medium' : 'high';

  return { score, level, insolvent: insolventCount, foreign: foreignCount };
}

function PathResults({ pathData, subgraphNodes }) {
  const { t } = useTranslation();
  const [compareMode, setCompareMode] = useState(false);

  if (!pathData || !pathData.found) {
    return null;
  }

  // Handle exploration mode differently
  if (pathData.exploration) {
    return (
      <div className="path-results">
        <h2>{t('results.exploration_title')}</h2>
        <div className="exploration-info">
          <p><strong>{t('results.entity_label')}</strong> {pathData.entity.name}</p>
          <p><strong>{t('results.type_label')}</strong> {pathData.entity.type}</p>
          <p><strong>{t('results.connections_label')}</strong> {pathData.neighbor_count}</p>
          <div className="exploration-hint">
            {t('results.expand_hint')}
          </div>
        </div>
      </div>
    );
  }

  const hasMultiplePaths = pathData.paths && pathData.paths.length >= 2;

  // Find entities unique to each path for highlighting in compare mode
  const getUniqueEntities = () => {
    if (!hasMultiplePaths) return {};
    const allPathEntities = pathData.paths.map(p =>
      new Set(p.details.map(s => s.name))
    );
    const uniqueMap = {};
    pathData.paths.forEach((p, i) => {
      const otherSets = allPathEntities.filter((_, j) => j !== i);
      const uniqueToThis = new Set();
      p.details.forEach(s => {
        const inOther = otherSets.some(set => set.has(s.name));
        if (!inOther) uniqueToThis.add(s.name);
      });
      uniqueMap[i] = uniqueToThis;
    });
    return uniqueMap;
  };

  if (compareMode && hasMultiplePaths) {
    const uniqueEntities = getUniqueEntities();

    return (
      <div className="path-results">
        <div className="compare-header">
          <h2>{t('results.path_comparison')}</h2>
          <button className="compare-btn active" onClick={() => setCompareMode(false)}>
            {t('results.back_to_list')}
          </button>
        </div>

        <div className="comparison-container">
          {pathData.paths.map((pathInfo, index) => {
            const risk = calcRiskScore(pathInfo, subgraphNodes);
            const unique = uniqueEntities[index] || new Set();

            return (
              <div key={index} className="comparison-card">
                <div className="comparison-card-header">
                  <h4>{t('results.path_number', { index: index + 1 })}</h4>
                  <span className={`risk-badge ${risk.level}`}>
                    {risk.level === 'low' ? t('results.low_risk') : risk.level === 'medium' ? t('results.med_risk') : t('results.high_risk')}
                  </span>
                </div>

                <div className="comparison-stats">
                  <div className="comp-stat">
                    <span className="comp-stat-value">{pathInfo.length}</span>
                    <span className="comp-stat-label">{t('results.steps_stat')}</span>
                  </div>
                  <div className="comp-stat">
                    <span className="comp-stat-value">{pathInfo.details.length}</span>
                    <span className="comp-stat-label">{t('results.entities_stat')}</span>
                  </div>
                  <div className="comp-stat">
                    <span className="comp-stat-value">{risk.score}</span>
                    <span className="comp-stat-label">{t('results.risk_stat')}</span>
                  </div>
                </div>

                {risk.score > 0 && (
                  <div className="risk-details">
                    {risk.insolvent > 0 && <span className="risk-tag insolvent">{t('results.insolvent_tag', { count: risk.insolvent })}</span>}
                    {risk.foreign > 0 && <span className="risk-tag foreign">{t('results.foreign_tag', { count: risk.foreign })}</span>}
                  </div>
                )}

                <div className="entity-list-compact">
                  {pathInfo.details.map((step, si) => (
                    <div key={si} className={`compact-entity ${unique.has(step.name) ? 'unique' : ''}`}>
                      <span className="compact-icon">{step.type === 'company' ? '\u{1F3E2}' : '\u{1F464}'}</span>
                      <span className="compact-name">{step.name}</span>
                      {step.relationship_to_next && (
                        <span className="compact-rel">{step.relationship_to_next}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="path-results">
      <div className="compare-header">
        <h2>{t('results.paths_found', { count: pathData.count })} <HelpTip text={t('results.risk_tip')} /></h2>
        {hasMultiplePaths && (
          <button className="compare-btn" onClick={() => setCompareMode(true)}>
            {t('results.compare_paths')}
          </button>
        )}
      </div>

      {pathData.paths.map((pathInfo, index) => {
        const risk = calcRiskScore(pathInfo, subgraphNodes);

        return (
          <div key={index} className="path-card">
            <div className="path-card-header">
              <h3>{pathInfo.length === 1 ? t('results.path_header', { index: index + 1, length: pathInfo.length }) : t('results.path_header_plural', { index: index + 1, length: pathInfo.length })}</h3>
              <span className={`risk-badge ${risk.level}`}>
                {risk.level === 'low' ? t('results.low') : risk.level === 'medium' ? t('results.med') : t('results.high')}
              </span>
            </div>

            <div className="path-steps">
              {pathInfo.details.map((step, stepIndex) => (
                <div key={stepIndex} className="step">
                  <div className="step-node">
                    <span className={`node-badge ${step.type}`}>
                      {step.type === 'company' ? '\u{1F3E2}' : '\u{1F464}'}
                    </span>
                    <div className="step-info">
                      <strong>{step.name}</strong>
                      <span className="step-type">{step.type}</span>
                    </div>
                  </div>

                  {step.relationship_to_next && (
                    <div className="step-arrow">
                      <span className="relationship-label">
                        {step.relationship_to_next}
                      </span>
                      <span className="arrow">&darr;</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default PathResults;
